import { useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';

import classNames from 'classnames';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';
import { moreDescriptiveAriaLabel } from '@/utils/utils';
import beTwMerge from '@/utils/beTwMerge';

import { Action } from '@/components/SingleImageTile/SingleImageTile';
import Button from '@/components/homepage/Button/Button';
import DropdownButton from '@/components/homepage/DropdownButton';
import RichText from '../../RichText/RichText';
import Title from '../../Title/Title';

interface ITitle {
  content: string;
  titleHTMLTag?: 'h1' | 'h2' | 'h3';
  titleSize?: 'extraLarge' | 'large' | 'medium' | 'small';
  titleWeight?: 'light' | 'normal' | 'medium' | 'bold' | 'black';
}

interface IDiamondCharacteristicsDiagramProps {
  /**
   * Optional large header.
   * - content*: The content of the header
   * - titleHTMLTag: Heading level that will be used to render the optional header. Defaults to 'h2'
   * - titleSize: Select one of the styles 'extraLarge' | 'large' | 'medium' | 'small'.
   * - titleWeight: Optional header weight. Defaults to 'normal'
   */
  optionalLargeHeader?: ITitle;
  /**
   * Turn on to add margin to the bottom of the component
   */
  margin?: boolean;
  /**
   * Give to the component an anchor link id
   */
  anchorLinkID?: string;
  /**
   * To position the content either to the left or to the right
   */
  contentPosition: string;
  /**
   * To position the image either to the center or to the start
   */
  mediaAlignment: 'start' | 'center';
  /**
   *  Rich text content of the Block
   */
  content: string;
  /**
   * The list of cta buttons
   * Props.
   * - text*: Text of the button
   * - url*: url to link to
   * - newTab*: If the link should open in a new tab
   */
  actions?: Action[];
}

const Container = styled.div`
  display: flex;
  justify-content: center;

  @media (min-width: 768px) {
    .imitate-table-up > div {
      display: table-cell;
      vertical-align: middle;
      float: none;
    }
  }

  @media (min-width: 768px) {
    .col-sm-6 {
      width: 50%;
    }
  }
  @media (min-width: 768px) {
    .col-sm-6 {
      float: left;
    }
  }

  @media (max-width: 767px) {
    .col-xs-offset-1 {
      margin-left: 8.333333333333332%;
    }
  }
  .col-xs-10 {
    width: 83.33333333333334%;
    float: left;
  }
  .col-xs-10,
  .col-sm-6,
  .col-md-5 {
    position: relative;
    min-height: 1px;
    padding-right: 15px;
    padding-left: 15px;
  }

  .noUi-horizontal.noUiSlider--gray {
    height: 1px;
  }

  .noUi-horizontal {
    background: none repeat scroll 0 0 #e1e1e1;
    border: 0 none;
    border-radius: 10px;
    /* box-shadow: 0 0 3px #999999 inset; */
    height: 6px;
  }
  .noUi-background {
    border-radius: 10px;
    background: none;
  }
  .noUi-target {
    border-radius: 4px;
    border: 1px solid #d3d3d3;
    box-shadow:
      inset 0 1px 1px #f0f0f0,
      0 3px 6px -5px #bbb;
  }
  .noUi-background {
    background: #fafafa;
    box-shadow: inset 0 1px 1px #f0f0f0;
  }

  .noUi-horizontal {
    height: 18px;
  }

  .noUi-horizontal.noUiSlider--gray .noUi-base {
    background-color: #8f8f8f;
  }

  .noUi-base {
    background: #183e40;
    width: 100%;
    height: 100%;
    position: relative;
  }

  .noUi-origin {
    border-radius: 2px;
  }

  .noUi-origin {
    position: absolute;
    right: 0;
    top: 0;
    left: 0;
    bottom: 0;
  }

  .ui-slider-four li {
    position: absolute;
    height: 10px;
    width: 10px;
    z-index: 1;
    background: #ffffff;
    border: 1px solid #8f8f8f;
    border-radius: 10px;
    top: -4px;
    margin-left: -5px;
    cursor: pointer;

    &.active {
      background: #183e40;
      border-color: #183e40;
    }
  }

  .ui-slider-four {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .ui-slider-four li.fore1 {
    left: 0;
  }

  .ui-slider-four li.fore2 {
    left: 25%;
  }

  .ui-slider-four li.fore3 {
    left: 50%;
  }

  .ui-slider-four li.fore4 {
    left: 75%;
  }

  .ui-slider-four li.fore5 {
    left: 100%;
  }

  .modal-clarity-data {
    padding: 10px 0 0;
    text-transform: capitalize;
  }

  .mt10 {
    margin-top: 10px;
  }

  .clearfix:before,
  .clearfix:after {
    display: table;
    content: ' ';
  }

  .modal-slider-four-data li {
    width: 25%;
  }

  .modal-clarity-data li {
    float: left;
    list-style: none;
    font-size: 12px;
    line-height: 1em;
  }

  .modal-clarity-data li span {
    display: block;
    position: relative;
    left: -50%;
    text-align: center;
    white-space: nowrap;
  }

  .modal-clarity-data li.last span {
    left: auto;
    width: 60px;
    right: -30px;
    float: right;
  }

  .modal-slider-four-data .last {
    margin-left: -25%;
  }

  .clearfix:after {
    clear: both;
  }

  .pt-15 {
    padding-top: 15px;
  }

  .modal-slider-tooltip2 .tooltip {
    display: block;
    opacity: 1;
    position: relative;
    z-index: 2;
    padding: 10px 0 0;
  }
  .tooltip.bottom {
    padding: 5px 0;
    margin-top: 3px;
  }
  .tooltip {
    position: absolute;
    z-index: 1030;
    display: block;
    font-size: 12px;
    line-height: 1.4;
    opacity: 0;
    filter: alpha(opacity=0);
    visibility: visible;
  }

  .modal-slider-tooltip2 .tooltip-inner {
    max-width: 100%;
    text-align: center;
    background-color: #183e40;
    clear: both;
    padding: 1em 2em;
    color: white;
    border-radius: 0;
  }

  .tooltip-inner {
    max-width: 200px;
    padding: 3px 8px;
    color: #fff;
    text-align: center;
    text-decoration: none;
    background-color: #000;
    border-radius: 4px;
  }

  .modal-slider-tooltip2 .tooltip-inner .headline {
    font-size: 16px;
  }

  .ref-paragraph p {
    letter-spacing: 0.025em;
  }

  @media (min-width: 768px) {
    .ref-paragraph p {
      margin-bottom: 1em;
    }
  }
`;

export default function DiamondCharacteristicsDiagram({
  contentPosition,
  mediaAlignment,
  optionalLargeHeader,
  anchorLinkID,
  margin = false,
  content,
  actions,
}: IDiamondCharacteristicsDiagramProps) {
  const diamondCuts = [
    {
      title: 'Fair',
      description:
        'Good quality cut which reflects some light while maximizing weight. While not as brilliant as a good cut, still a quality diamond.',
      imageURL: 'https://image.brilliantearth.com/static/img/diamond-search/diamond-cut/cut-scale-1-fair.png',
    },
    {
      title: 'Good',
      description:
        'Premium quality cut to optimize the size without sacrificing quality or beauty. Reflects most light that enters.',
      imageURL: 'https://image.brilliantearth.com/static/img/diamond-search/diamond-cut/cut-scale-2-good.png',
    },
    {
      title: 'Very Good',
      description:
        'Superior quality cut that reflects nearly as much light as the ideal cut while at a substantially lower cost.',
      imageURL: 'https://image.brilliantearth.com/static/img/diamond-search/diamond-cut/cut-scale-3-very-good.png',
    },
    {
      title: 'Ideal',
      description:
        'Exquisite quality cut to create the optimal combination of brilliance and fire. Reflects nearly all light that enters the diamond.',
      imageURL: 'https://image.brilliantearth.com/static/img/diamond-search/diamond-cut/cut-scale-4-ideal.png',
    },
    {
      title: 'Super Ideal',
      description:
        'Cut to the most exacting standards. These diamonds have the most desirable dimensions and are proportioned to return the maximum possible light.',
      imageURL: 'https://image.brilliantearth.com/static/img/diamond-search/diamond-cut/cut-scale-5-super-ideal.png',
    },
  ];
  const [activeCut, setActiveCut] = useState<number>(0);
  const formattedActions = actions?.map(action => ({
    ...action,
    // @ts-ignore
    color: action.color === 'dark' ? 'dark-green' : 'white',
  }));

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      className={classNames('tw-w-[85%] tablet:tw-w-[85%] tw-max-w-[1440px] tw-mx-auto', getComponentMargin(margin))}
    >
      {optionalLargeHeader?.content && (
        <Title
          component={optionalLargeHeader?.titleHTMLTag || 'h2'}
          weight={optionalLargeHeader?.titleWeight}
          size={optionalLargeHeader?.titleSize}
          alignment="center"
        >
          {optionalLargeHeader?.content}
        </Title>
      )}
      <div
        className={classNames(
          'tw-flex tw-items-center lg:tw-mx-auto tw-flex-col lg:tw-flex-row tw-max-w-[1172px] tw-w-full lg:tw-w-full tw-gap-x-[64px] lg:tw-max-w-[1440px]',
          {
            'lg:tw-flex-row-reverse': contentPosition === 'right',
            'lg:tw-items-start ': mediaAlignment === 'start',
          }
        )}
      >
        <div className="tw-w-full lg:tw-w-1/2">
          <Container>
            <div className="col-md-5 col-sm-6 col-xs-10 modal-slider-tips diamond-cut-slider">
              <div className="search-noUiSlider-wrap">
                <div className="clarity-scale pb-40 tw-pb-be-10">
                  <div className="tw-relative tw-aspect-[202/152] tw-max-w-[202px] img-responsive center-block">
                    <Image
                      fill
                      src={diamondCuts[activeCut].imageURL}
                      alt={`Diamond cut characteristics - ${diamondCuts[activeCut].title}`}
                    />
                  </div>
                </div>

                <div id="modal_cuts" className="search-noUiSlider">
                  <div
                    id="modal_cuts_slider"
                    className="noUiSlider noUiSlider--gray noUi-target noUi-ltr noUi-horizontal noUi-background"
                    style={{
                      height: '1px',
                      background: 'none repeat scroll 0 0 #E1E1E1',
                      borderRadius: '10px',
                      boxShadow: 'inset 0 1px 1px #F0F0F0, 0 3px 6px -5px #BBB',
                      boxSizing: 'border-box',
                    }}
                  >
                    <div
                      className="noUi-base"
                      style={{
                        backgroundColor: '#8f8f8f',
                        background: '#183E40',
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                      }}
                    >
                      <ol className="ui-slider-four">
                        <li onClick={() => setActiveCut(0)} className={`fore1 ${activeCut === 0 ? 'active' : ''}`} />
                        <li onClick={() => setActiveCut(1)} className={`fore2 ${activeCut === 1 ? 'active' : ''}`} />
                        <li onClick={() => setActiveCut(2)} className={`fore3 ${activeCut === 2 ? 'active' : ''}`} />
                        <li onClick={() => setActiveCut(3)} className={`fore4 ${activeCut === 3 ? 'active' : ''}`} />
                        <li onClick={() => setActiveCut(4)} className={`fore5 ${activeCut === 4 ? 'active' : ''}`} />
                      </ol>
                    </div>
                  </div>

                  <ul className="modal-clarity-data modal-slider-four-data mt10 clearfix">
                    {diamondCuts.map(cut => (
                      <li
                        key={cut.title.replace(' ', '-')}
                        className={diamondCuts[4].title === cut.title ? 'last' : ''}
                      >
                        <span>
                          {cut.title.split(' ').length !== 1 ? (
                            cut.title
                          ) : (
                            <>
                              {cut.title.split(' ')[0]}
                              <br />
                              {cut.title.split(' ')[1]}
                            </>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="modal_cuts_slider modal-slider-tooltip2 pt-15">
                    <div role="tooltip" className="tooltip bottom" style={{ zIndex: 2 }}>
                      <div className="tooltip-inner tw-min-h-[132px]">
                        <h3 className="headline tw-text-white">{diamondCuts[activeCut].title}</h3>
                        <p className="ls-em25">{diamondCuts[activeCut].description}</p>
                        <p />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
        <div className="lg:tw-w-1/2 lg:!tw-max-w-[1172px] lg:tw-mt-0 tw-mt-be-6">
          <RichText content={content} />
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 md:tw-flex-row tw-gap-2 md:tw-gap-4 tw-w-full tw-mt-12">
            {formattedActions?.slice(0, 4).map((action, i) => {
              const { label, color, type } = action;

              if (action.actions) {
                const { actions: subActions } = action;
                return (
                  <DropdownButton
                    key={i}
                    label={label}
                    options={subActions}
                    // @ts-ignore
                    color={color}
                    type={type}
                    className={beTwMerge(
                      `tw-basis-[calc(50%-8px)] tw-w-full tw-text-center tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis`
                    )}
                  />
                );
              }
              const { onClick, href } = action;
              return (
                <Button
                  className={beTwMerge(
                    `tw-basis-[calc(50%-8px)] tw-max-w-none tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis md:tw-overflow-hidden`
                  )}
                  ariaLabel={moreDescriptiveAriaLabel(optionalLargeHeader?.content, label)}
                  key={i}
                  label={label}
                  // @ts-ignore
                  color={color}
                  type={type}
                  href={href}
                  onClick={onClick}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
